import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getUserAccessAuthorization } from 'src/api/app-sync-services';
import { Status } from 'src/models/AuthContextModels';
import { CurrentUserAccessDetails, UserAccessEntity, UserAccessForCurrentBusinessGroup } from 'src/models/XptUsersModel';
import { RootState } from 'src/store/store';
import { getCurrentUserAccessDetails, getUserCostCenters, getUserProductLines, processUserPermissions } from './AccessAuthorizationUtils';

interface AccessAndAuthorizationState {
  xptUsersLoadingStatus: Status;
  xptUsers: UserAccessEntity[];

  xptActiveUsersWithCorpSegments: UserAccessEntity[];
  xptActiveUsersWithCorpSegmentsLoadingStatus: Status;

  // currentUserAccessDetails is widely used at Admin Console / Dev / where user is not in particular Business Group
  currentUserAccessLoadingStatus: Status;
  currentUserAccessDetails: CurrentUserAccessDetails | null;
  currentUserNotFound: boolean;

  // Once user is in particular business group, userAccessForCurrentBusinessGroup is utilized
  userAccessForCurrentBusinessGroup: UserAccessForCurrentBusinessGroup | null;
  userCostCentersForCurrentBusinessGroup: string[];
  userProductLinesForCurrentBusinessGroup: string[];
  userAccessForCurrentBusinessGroupStatus: Status;
}

const initialState: AccessAndAuthorizationState = {
  xptUsers: [],
  xptUsersLoadingStatus: Status.NotInitiated,

  xptActiveUsersWithCorpSegments: [],
  xptActiveUsersWithCorpSegmentsLoadingStatus: Status.NotInitiated,

  currentUserAccessLoadingStatus: Status.NotInitiated,
  currentUserAccessDetails: null,
  currentUserNotFound: false,

  userAccessForCurrentBusinessGroup: null,
  userCostCentersForCurrentBusinessGroup: [],
  userProductLinesForCurrentBusinessGroup: [],
  userAccessForCurrentBusinessGroupStatus: Status.NotInitiated
};

// Async thunk to fetch all user access and authorization data
export const fetchUserAccessAndAuthorization = createAsyncThunk<
  UserAccessEntity[],
  { userAlias: string; isInitiallyDev: boolean },
  { rejectValue: string }
>('accessAndAuthorization/fetchUserAccessAndAuthorization', async ({ userAlias, isInitiallyDev }, { rejectWithValue }) => {
  try {
    const userAccessData: UserAccessEntity[] = await getUserAccessAuthorization();

    // Commenting this out to get current dev user in Budget Owner dropdown list
    // Since developer access is being used from "Access Controller" (Modal popup), system wont consider user information from here.
    // if (isInitiallyDev) {
    //   const filteredUsersList = userAccessData.filter((userAccess) => userAccess.user_alias !== userAlias);
    //   return filteredUsersList;
    // }

    return userAccessData;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    return rejectWithValue(`Failed to fetch user access and authorization data: ${errorMessage}`);
  }
});

// Async thunk to fetch all user access and authorization data
export const fetchAllActiveUserAccessWithCorpSegments = createAsyncThunk<UserAccessEntity[], void, { state: RootState; rejectValue: string }>(
  'accessAndAuthorization/fetchAllActiveUserAccessWithCorpSegments',
  async (_, { getState, rejectWithValue }) => {
    try {
      // Get the state from the store
      const rootState = getState();
      const xptUsers: UserAccessEntity[] = rootState.xptAccessAndAuthorizationStore.xptUsers;
      const businessGroups = rootState.businessGroupStore.businessGroups;
      const activeBusinessGroups = businessGroups.filter((group) => group.item_metadata.is_active);
      const ccAndPLForAllBusinessGroups = rootState.corpSegmentsStore.masterCostCenterDropdownValuesForAllBusinessGroups;

      const parsedUsersWithCorpSegmentAccess: UserAccessEntity[] = xptUsers.map((userAccess) => {
        return {
          ...userAccess,
          corp_segment_access: processUserPermissions(userAccess, activeBusinessGroups, ccAndPLForAllBusinessGroups).corp_segment_access
        };
      });

      return parsedUsersWithCorpSegmentAccess;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      return rejectWithValue(`Failed to fetch user access and authorization data: ${errorMessage}`);
    }
  }
);

// Async thunk to fetch current user authorization data
export const fetchCurrentUserAuthorization = createAsyncThunk<
  { currentUserAccessDetails: CurrentUserAccessDetails | null; currentUserNotFound: boolean },
  { userAlias: string; isDev: boolean },
  { state: RootState; rejectValue: string }
>('accessAndAuthorization/fetchCurrentUserAuthorization', async ({ userAlias, isDev }, { getState, rejectWithValue }) => {
  try {
    // Get the state from the store
    const rootState = getState();
    const xptUsers = rootState.xptAccessAndAuthorizationStore.xptUsers;
    const businessGroups = rootState.businessGroupStore.businessGroups;
    const ccAndPLForAllBusinessGroups = rootState.corpSegmentsStore.masterCostCenterDropdownValuesForAllBusinessGroups;

    const userAccess = xptUsers.filter((user) => user.user_alias?.toLowerCase() === userAlias?.toLowerCase() && user.is_active);

    const activeBusinessGroups = businessGroups.filter((group) => group.item_metadata.is_active);

    // Call the utility function.
    // Note: A user without access to any business group is a valid outcome, not an error.
    const currentUserAccessDetails = getCurrentUserAccessDetails(userAlias, isDev, userAccess, activeBusinessGroups, ccAndPLForAllBusinessGroups);

    // console.log('currentUserAccessDetails ', JSON.stringify(currentUserAccessDetails));

    // Return the details as expected by the thunk's fulfilled case
    return currentUserAccessDetails;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    return rejectWithValue(`Failed to fetch current user details: ${errorMessage}`);
  }
});

const accessAndAuthorizationSlice = createSlice({
  name: 'accessAndAuthorization',
  initialState,
  reducers: {
    // Loads at Business Group Wrapper
    setUserAccessForCurrentBusinessGroup(state, action: PayloadAction<UserAccessForCurrentBusinessGroup | null>) {
      state.userAccessForCurrentBusinessGroup = action.payload;
      state.userCostCentersForCurrentBusinessGroup = action.payload ? getUserCostCenters(action.payload) : [];
      state.userProductLinesForCurrentBusinessGroup = action.payload ? getUserProductLines(action.payload) : [];
    },
    // Loads at Business Group Wrapper
    setUserAccessForCurrentBusinessGroupStatus(state, action: PayloadAction<Status>) {
      state.userAccessForCurrentBusinessGroupStatus = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchUserAccessAndAuthorization
      .addCase(fetchUserAccessAndAuthorization.pending, (state) => {
        state.xptUsers = [];
        state.xptUsersLoadingStatus = Status.Loading;
      })
      .addCase(fetchUserAccessAndAuthorization.fulfilled, (state, action: PayloadAction<UserAccessEntity[]>) => {
        state.xptUsers = action.payload;
        state.xptUsersLoadingStatus = Status.Completed;
      })
      .addCase(fetchUserAccessAndAuthorization.rejected, (state, action) => {
        state.xptUsers = [];
        state.xptUsersLoadingStatus = Status.Failed;
      })
      // Handle fetchAllActiveUserAccessWithCorpSegments
      .addCase(fetchAllActiveUserAccessWithCorpSegments.pending, (state) => {
        state.xptActiveUsersWithCorpSegments = [];
        state.xptActiveUsersWithCorpSegmentsLoadingStatus = Status.Loading;
      })
      .addCase(fetchAllActiveUserAccessWithCorpSegments.fulfilled, (state, action: PayloadAction<UserAccessEntity[]>) => {
        state.xptActiveUsersWithCorpSegments = action.payload;
        state.xptActiveUsersWithCorpSegmentsLoadingStatus = Status.Completed;
      })
      .addCase(fetchAllActiveUserAccessWithCorpSegments.rejected, (state, action) => {
        state.xptActiveUsersWithCorpSegments = [];
        state.xptActiveUsersWithCorpSegmentsLoadingStatus = Status.Failed;
      })
      // Handle fetchCurrentUserAuthorization
      .addCase(fetchCurrentUserAuthorization.pending, (state) => {
        state.currentUserAccessDetails = null;
        state.currentUserNotFound = false;
        state.currentUserAccessLoadingStatus = Status.Loading;
      })
      .addCase(
        fetchCurrentUserAuthorization.fulfilled,
        (state, action: PayloadAction<{ currentUserAccessDetails: CurrentUserAccessDetails | null; currentUserNotFound: boolean }>) => {
          state.currentUserAccessDetails = action.payload.currentUserAccessDetails;
          state.currentUserNotFound = action.payload.currentUserNotFound;
          state.currentUserAccessLoadingStatus = Status.Completed;
        }
      )
      .addCase(fetchCurrentUserAuthorization.rejected, (state, action) => {
        state.currentUserAccessDetails = null;
        state.currentUserNotFound = true;
        state.currentUserAccessLoadingStatus = Status.Failed;
      });
  }
});

export const { setUserAccessForCurrentBusinessGroup, setUserAccessForCurrentBusinessGroupStatus } = accessAndAuthorizationSlice.actions;
export default accessAndAuthorizationSlice.reducer;
